import { pageTitle } from '@palaccount/config';
import { Factory, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatQty } from '@/lib/format';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerMessage, refusalMessage } from '@/lib/server-messages';
import { useCompanyToday } from '@/lib/session';

import { Typeahead } from '@/components/ui/typeahead';
import { useItemPicker } from '../inventory/item-picker';
import { Modal } from '../inventory/modal';
import { useWarehouses } from '../inventory/stock-api';
import { useWorkOrderMutations, useWorkOrders } from './api';
import { ManufacturingTabs } from './manufacturing-tabs';
import { WorkOrderStatusPill } from './work-order-page';

/**
 * MFG-02's list. The spec draws one work order rather than a register, so this
 * is the way in: the jobs, newest first, and the New button that creates one.
 *
 * BR-MFG-02-01 lives on the server: an item with no ACTIVE bill of materials
 * is refused by name ("Desk Lamp has no active bill of materials. Create one
 * first."), and that sentence is what this form shows.
 */
export function WorkOrdersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** NFR-42 — the page rides in the URL. */
  const paging = usePagedState();
  const { data, isPending, isError } = useWorkOrders(paging.page, paging.pageSize);
  const workOrders = data?.workOrders;
  const range = usePageRange(data, paging.pageSize);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('mfg.workOrders'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <ManufacturingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('mfg.workOrders')}</h2>
        <Gated permission="manufacturing.create" reasonKey="perm.cannot.createWorkOrders">
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (workOrders ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('mfg.noJobs')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mfg.colWorkOrder')}</TableHead>
                  <TableHead>{t('mfg.finishedItem')}</TableHead>
                  <TableHead className="text-end">{t('mfg.qtyToProduce')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colProgress')}</TableHead>
                  <TableHead className="text-end">{t('mfg.plannedCost')}</TableHead>
                  <TableHead>{t('mfg.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(workOrders ?? []).map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer odd:bg-muted/30"
                    onClick={() => void navigate(`/manufacturing/work-orders/${job.id}`)}
                  >
                    <TableCell>
                      <Link
                        to={`/manufacturing/work-orders/${job.id}`}
                        className="font-mono text-sm underline"
                      >
                        {job.code}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.finishedItemName}
                      <Ltr
                        value={` · ${job.bomLabel}`}
                        className="text-xs text-muted-foreground"
                      />
                    </TableCell>
                    {/*
                      `N-4`, and `N-14`'s "on the row, never the header": the
                      job's target in the finished item's OWN unit. `work_orders`
                      stores no unit of its own, so it is that item's STOCK unit
                      — the shelf the good output is received onto
                      (`BR-MFG-02-16`), which is what `outputUnitSymbol` carries.
                    */}
                    <TableCell className="text-end">
                      <Qty
                        value={job.qtyToProduce}
                        precision={job.outputUnitPrecision}
                        unit={job.outputUnitSymbol}
                        className="text-sm"
                      />
                    </TableCell>
                    {/*
                      Field 29's pair, at the unit's decimals and WITHOUT the
                      symbol: the cell to its left is the same job's target in
                      the same unit, and repeating it here would print it three
                      times on one row. The two halves of a fraction are one
                      measurement of one thing — MFG-02's own job screen writes
                      the symbol once after the run for that reason; here the
                      run's other half is the adjacent cell.
                    */}
                    <TableCell className="text-end">
                      <Ltr
                        value={`${formatQty(job.producedQty, job.outputUnitPrecision)} / ${formatQty(
                          job.qtyToProduce,
                          job.outputUnitPrecision,
                        )}`}
                        className="font-mono tabular-nums text-sm"
                      />
                    </TableCell>
                    <TableCell className="text-end">
                      <Amount value={job.cost.plannedCost} className="text-sm" />
                    </TableCell>
                    <TableCell>
                      <WorkOrderStatusPill status={job.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('mfg.workOrders')}
            />
          ) : null}
        </CardContent>
      </Card>

      {creating ? <NewWorkOrderModal onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function NewWorkOrderModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const today = useCompanyToday();
  const navigate = useNavigate();
  const { create } = useWorkOrderMutations();
  const { data: warehouses } = useWarehouses();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    finishedItemId: '',
    qtyToProduce: '1',
    warehouseId: '',
    plannedStart: today,
    plannedEnd: today,
    linkedSalesOrder: '',
  });
  // GEN-50 — the finished-goods picker, searched. `type: 'product'` is the
  // server's narrowing: a job makes a product, never a service.
  const itemPicker = useItemPicker([form.finishedItemId], { type: 'product' });

  return (
    <Modal title={t('mfg.newWorkOrder')} onClose={onClose}>
      {error !== null ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="wo-item">{t('mfg.finishedItem')}</Label>
          <Typeahead
            id="wo-item"
            label={t('mfg.finishedItem')}
            value={form.finishedItemId || null}
            onChange={(id) => setForm({ ...form, finishedItemId: id ?? '' })}
            options={itemPicker.options}
            selected={itemPicker.optionFor(form.finishedItemId)}
            query={itemPicker.query}
            onQueryChange={itemPicker.setQuery}
            placeholder={t('common.select')}
            emptyLabel={t('picker.noMatchingItem')}
            loading={itemPicker.loading}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="wo-qty">{t('mfg.qtyToProduce')}</Label>
          <Input
            id="wo-qty"
            className="font-mono"
            value={form.qtyToProduce}
            onChange={(event) => setForm({ ...form, qtyToProduce: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          {/* Field 10: ONE warehouse for the whole job — components come out of
              it and finished goods go back into it. */}
          <Label htmlFor="wo-warehouse">{t('mfg.warehouse')}</Label>
          <NativeSelect
            id="wo-warehouse"
            value={form.warehouseId}
            onChange={(event) => setForm({ ...form, warehouseId: event.target.value })}
          >
            <option value="">{t('common.select')}</option>
            {(warehouses ?? []).map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          {/* Field 13: free text, max 32 — not a picker (§5.2). */}
          <Label htmlFor="wo-so">{t('mfg.linkedSalesOrder')}</Label>
          <Input
            id="wo-so"
            maxLength={32}
            value={form.linkedSalesOrder}
            onChange={(event) => setForm({ ...form, linkedSalesOrder: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="wo-start">{t('mfg.plannedStart')}</Label>
          <Input
            id="wo-start"
            type="date"
            value={form.plannedStart}
            onChange={(event) => setForm({ ...form, plannedStart: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="wo-end">{t('mfg.plannedEnd')}</Label>
          <Input
            id="wo-end"
            type="date"
            value={form.plannedEnd}
            onChange={(event) => setForm({ ...form, plannedEnd: event.target.value })}
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={form.finishedItemId === '' || form.warehouseId === ''}
          onClick={() => {
            setError(null);
            void create
              .mutateAsync({
                finishedItemId: form.finishedItemId,
                qtyToProduce: form.qtyToProduce,
                warehouseId: form.warehouseId,
                plannedStart: form.plannedStart,
                plannedEnd: form.plannedEnd,
                ...(form.linkedSalesOrder === ''
                  ? {}
                  : { linkedSalesOrder: form.linkedSalesOrder }),
              })
              .then((job) => {
                onClose();
                void navigate(`/manufacturing/work-orders/${job.id}`);
              })
              // D46: the cause rides along — a structured refusal (noActiveBom
              // names the item) localizes by key+params; anything else keeps
              // the server's own sentence.
              .catch((caught: unknown) =>
                setError(localizeServerMessage(t, refusalMessage(caught), caught)),
              );
          }}
        >
          <Factory aria-hidden /> {t('common.create')}
        </Button>
      </div>
    </Modal>
  );
}
